import { history } from "instantsearch.js/es/lib/routers";
import type { UiState } from "instantsearch.js";

type RouteState = {
  q?: string;
  category?: string[];
};

export const safeNavigate = (url: string): void => {
  if (!url) return;

  try {
    if (url.startsWith("/")) {
      window.location.href = url;
      return;
    }

    const targetUrl = new URL(url, window.location.origin);
    if (targetUrl.origin === window.location.origin) {
      window.location.href = url;
    }
  } catch {
    // Invalid URL, do nothing
  }
};

export const parseURLHash = (
  qsModule: { parse: (hash: string) => unknown },
  location: { hash: string },
) => {
  const hash = extractHashFromLocation(location);
  return qsModule.parse(hash);
};

export const createURLWithHash = (
  qsModule: { stringify: (state: unknown) => string },
  location: { pathname: string },
  routeState: unknown,
) => {
  const hash = qsModule.stringify(routeState);
  const hashPart = createHashPart(hash);
  return location.pathname + hashPart;
};

export const createHashPart = (hash: string): string => {
  return hash ? `#${hash}` : "";
};

export const extractHashFromLocation = (location: { hash: string }): string => {
  return location.hash.slice(1);
};

export const routingConfig = (
  indexName: string,
): {
  router: ReturnType<typeof history>;
  stateMapping: {
    stateToRoute(uiState: UiState): RouteState;
    routeToState(routeState: unknown): UiState;
  };
} => ({
  router: history({
    cleanUrlOnDispose: false,
    parseURL: ({ qsModule, location }) => parseURLHash(qsModule, location),
    createURL: ({ qsModule, location, routeState }) =>
      createURLWithHash(qsModule, location, routeState),
  }),
  stateMapping: {
    stateToRoute(uiState: UiState): RouteState {
      const indexState = uiState[indexName];
      const query = indexState?.query;
      const menu = indexState?.menu;
      const categoryFilter = menu?.['category'] as
        | string
        | string[]
        | undefined;

      const routeState: RouteState = {};
      if (query) routeState.q = query;
      if (categoryFilter) {
        routeState.category = Array.isArray(categoryFilter)
          ? categoryFilter
          : [categoryFilter];
      }

      return routeState;
    },
    routeToState(routeState: unknown): UiState {
      const route = routeState as RouteState;
      const indexState: {
        query: string;
        menu?: { category: string };
      } = {
        query: route.q ?? "",
      };

      if (route.category && route.category.length > 0) {
        indexState.menu = {
          category: route.category[0],
        };
      }

      return {
        [indexName]: indexState,
      };
    },
  },
});
